import FileService from "#src/components/Services/FileService";
import {
  FileDownloadMetaSchema,
  FileScanStatus,
  TERMINAL_FILE_SCAN_STATUSES,
  type FileDownloadMetaType,
} from "@validereinc/domain";
import { invariant } from "@validereinc/utilities";

/** Milliseconds between scan-status polls while a scan is still pending. */
export const DEFAULT_SCAN_POLL_INTERVAL_MS = 1000;

/**
 * Fallback lifetime, in seconds, assumed for a pre-signed download URL when its
 * AWS `X-Amz-Expires` query param can't be read.
 */
const DEFAULT_PRESIGNED_URL_REFRESH_SECONDS = 10 * 60;

export type FileScanPollConfigType = {
  /**
   * Maximum number of times to poll for the file scan status. Defaults to 10.
   * Pass `Infinity` to poll indefinitely until the scan completes or `signal`
   * aborts — only safe when a `signal` is provided so polling can be stopped.
   */
  maxPolls?: number;
  /** Milliseconds to wait between file scan status checks. Defaults to 1000ms. */
  pollSleepTimeMs?: number;
  /**
   * Aborts polling when triggered, e.g. when the previewing component unmounts.
   * Required to safely poll with an infinite `maxPolls`.
   */
  signal?: AbortSignal | null;
  /**
   * Predicate deciding when polling can stop. Defaults to
   * {@link isFileScanComplete} (safe and moved to the archived bucket), which
   * suits download/preview. Upload flows can pass {@link isFileScanTerminal} to
   * stop as soon as the scan resolves to safe/unsafe.
   */
  isComplete?: (fileData: FileDownloadMetaType) => boolean;
};

/**
 * Normalizes an abort signal's reason into an {@link Error} to reject/throw with.
 */
const toAbortError = (signal?: AbortSignal | null): Error => {
  if (signal?.reason instanceof Error) {
    return signal.reason;
  }

  return new Error(
    typeof signal?.reason === "string"
      ? signal.reason
      : "File scan status polling was aborted."
  );
};

/**
 * Throws if the provided abort signal has already been aborted.
 */
const throwIfAborted = (signal?: AbortSignal | null): void => {
  if (signal?.aborted) {
    throw toAbortError(signal);
  }
};

/**
 * Sleeps for the given duration, rejecting early if the abort signal fires so
 * an in-flight poll loop can stop promptly instead of waiting out the timer.
 */
const sleepWithAbort = (
  ms: number,
  signal?: AbortSignal | null
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(toAbortError(signal));
      return;
    }

    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(toAbortError(signal));
    };

    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Fetches file download metadata for a single attempt and unwraps it from
 * the raw service response shape.
 */
export const fetchFileDownloadMeta = async (
  fileId: string,
  signal?: AbortSignal | null
): Promise<FileDownloadMetaType> => {
  const data = await FileService.getFileDownloadUrl(fileId, signal);

  invariant(data, "Could not get file url");

  const {
    data: {
      data: [fileData],
    },
  } = data;

  invariant(fileData, "Could not extract file data");

  return FileDownloadMetaSchema.parse(fileData);
};

/**
 * Post-scan archived S3 virtual-hosted hostname. Bucket names may include an
 * environment prefix and uniqueness suffix, e.g.
 * `data-platform-production-us-west-2-archived-c29f972a.s3.amazonaws.com`.
 */
const ARCHIVED_BUCKET_URL_PATTERN =
  /archived(?:-[a-z0-9]+)?\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com/i;

/**
 * Returns whether a download URI points at the post-scan archived S3 bucket.
 */
const isArchivedBucketUri = (uri?: string): boolean =>
  Boolean(uri && ARCHIVED_BUCKET_URL_PATTERN.test(uri));

/**
 * Returns whether file download metadata has reached a terminal scan state
 * that no longer needs scan/archived-bucket polling.
 *
 * Unsafe is terminal immediately — unsafe files do not send a uri in the response.
 * Safe requires the archived-bucket URI as well, because scan
 * status and bucket migration can finish at different times.
 */
export const isFileScanComplete = (fileData: FileDownloadMetaType): boolean => {
  if (fileData.file_scan_status === FileScanStatus.UNSAFE) {
    return true;
  }

  return (
    fileData.file_scan_status === FileScanStatus.SAFE &&
    isArchivedBucketUri(fileData.uri)
  );
};

/**
 * Returns whether a file's scan has reached a terminal state (safe or unsafe),
 * regardless of whether the archived-bucket URI is ready yet. Suits upload
 * flows that only need to know the scan verdict.
 */
export const isFileScanTerminal = (fileData: FileDownloadMetaType): boolean =>
  (TERMINAL_FILE_SCAN_STATUSES as readonly string[]).includes(
    fileData.file_scan_status
  );

/**
 * Fetches file download metadata, polling while scan status is still pending.
 */
export const fetchFileDownloadMetaWithScanPolling = async (
  fileId: string,
  pollConfig?: FileScanPollConfigType
): Promise<FileDownloadMetaType> => {
  const maxPolls = pollConfig?.maxPolls ?? 10;
  const pollSleepTimeMs = pollConfig?.pollSleepTimeMs ?? 1000;
  const signal = pollConfig?.signal;
  const isComplete = pollConfig?.isComplete ?? isFileScanComplete;
  let latestFileData: FileDownloadMetaType | undefined;
  let pollCount = 0;

  // keep polling while polling hasn't been aborted, the scan hasn't reached its
  // completion state, and the poll cap hasn't been reached
  while (
    !signal?.aborted &&
    !(latestFileData && isComplete(latestFileData)) &&
    pollCount < maxPolls
  ) {
    latestFileData = await fetchFileDownloadMeta(fileId, signal);
    pollCount += 1;

    if (isComplete(latestFileData)) {
      break;
    }

    await sleepWithAbort(pollSleepTimeMs, signal);
  }

  throwIfAborted(signal);

  invariant(
    latestFileData,
    "Could not extract file data after polling for scan status"
  );
  invariant(
    isComplete(latestFileData),
    "The file scan did not complete in time."
  );

  return latestFileData;
};

/**
 * Milliseconds until a safe file's pre-signed download URL should be refreshed,
 * derived from the AWS `X-Amz-Expires` query param when present (defaulting to
 * {@link DEFAULT_PRESIGNED_URL_REFRESH_SECONDS}).
 * @see {@link https://docs.aws.amazon.com/AmazonS3/latest/API/sigv4-query-string-auth.html}
 */
export const getPresignedUrlRefreshMs = (uri?: string): number => {
  if (!uri) {
    return DEFAULT_PRESIGNED_URL_REFRESH_SECONDS * 1000;
  }

  const params = new URLSearchParams(new URL(uri).search);

  return params.has("X-Amz-Expires")
    ? (Number(params.get("X-Amz-Expires")) ||
        DEFAULT_PRESIGNED_URL_REFRESH_SECONDS) * 1000
    : DEFAULT_PRESIGNED_URL_REFRESH_SECONDS * 1000;
};

export const IMPORT_SCAN_POLL_INTERVAL_MS = 3000;
export const MIN_IMPORT_SCAN_POLL_BUDGET_MS = 3 * 60 * 1000;
export const IMPORT_SCAN_POLL_BUDGET_MS_PER_MB = 2000;
export const MAX_IMPORT_SCAN_POLL_BUDGET_MS = 15 * 60 * 1000;

/**
 * Returns a scan-poll config for a dataset import upload, scaled by file
 * size (in bytes) so large uploads aren't wrongly reported as failed while
 * GuardDuty is still scanning them, bounded by
 * {@link MAX_IMPORT_SCAN_POLL_BUDGET_MS}.
 */
export const getImportScanPollConfig = (
  fileSizeBytes: number
): Required<Pick<FileScanPollConfigType, "maxPolls" | "pollSleepTimeMs">> => {
  const fileSizeMb = fileSizeBytes / (1024 * 1024);
  const budgetMs = Math.min(
    MIN_IMPORT_SCAN_POLL_BUDGET_MS +
      fileSizeMb * IMPORT_SCAN_POLL_BUDGET_MS_PER_MB,
    MAX_IMPORT_SCAN_POLL_BUDGET_MS
  );

  return {
    maxPolls: Math.ceil(budgetMs / IMPORT_SCAN_POLL_INTERVAL_MS),
    pollSleepTimeMs: IMPORT_SCAN_POLL_INTERVAL_MS,
  };
};

export const getFileDownloadMetaRefetchInterval = (
  fileData: FileDownloadMetaType | undefined,
  pollSleepTimeMs: number = DEFAULT_SCAN_POLL_INTERVAL_MS
): number | false => {
  if (!fileData) {
    return false;
  }

  if (!isFileScanComplete(fileData)) {
    return pollSleepTimeMs;
  }

  if (fileData.file_scan_status === FileScanStatus.UNSAFE) {
    return false;
  }

  return getPresignedUrlRefreshMs(fileData.uri);
};
